import { useI18nSubscription } from '../../../i18n/useI18nSubscription.js';
import { i18n } from '../../../../i18n/index.js';
// Представление в чате.
//
// Рисует то же, что страница агента представлений, — `ViewRenderer` по данным,
// уже ограниченным правами спросившего, — и даёт закрепить результат той же
// модалкой. Сниппет ничего не перезапрашивает: данные пришли в самом сообщении,
// а живым представление становится только после закрепления.
import { useState, type ReactElement } from 'react';
import { Button, Space, Tag } from 'antd';
import { Link } from 'react-router-dom';

import { SaveViewModal } from '../../../views/components/SaveViewModal.js';
import { ViewRenderer } from '../../../views/components/ViewRenderer.js';
import { useCreateSavedView } from '../../../views/hooks/useSavedViews.js';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';
import type { ViewResult } from '../../../../views/result.js';
import type { ViewSpec } from '../../../../views/spec.js';

interface ViewSnippetProps {
  view: Extract<StructuredMessagePayload, { type: 'view' }>['view'];
}

export function ViewSnippet({ view }: ViewSnippetProps): ReactElement {
  useI18nSubscription();
  // Кит переносит описание и данные как непрозрачные контейнеры; их устройство
  // знает только приложение, поэтому сужение типов происходит здесь.
  const spec = view.spec as unknown as ViewSpec;
  const result = view.result as unknown as ViewResult;
  const createView = useCreateSavedView();
  const [pinOpen, setPinOpen] = useState(false);
  const [savedViewId, setSavedViewId] = useState<string | null>(view.savedViewId ?? null);

  return (
    <div className="space-y-2" data-testid="structured-snippet-view">
      <ViewRenderer result={result} />
      <Space>
        {view.agentWidgetId !== undefined ? (
          <Link to="/dashboard">
            {i18n.t(
              'crm.features.features.ai.chat.components.snippets.viewsnippet.otkryt_na_dasborde',
            )}
          </Link>
        ) : savedViewId === null ? (
          <Button size="small" onClick={() => setPinOpen(true)}>
            {i18n.t('crm.features.features.ai.chat.components.snippets.viewsnippet.zakrepit')}
          </Button>
        ) : (
          <Link to={`/views/${savedViewId}`}>
            {i18n.t(
              'crm.features.features.ai.chat.components.snippets.viewsnippet.otkryt_zakreplennoe_predstavlenie',
            )}
          </Link>
        )}
        {view.mode === 'fixtures' ? (
          // Живой модели за шлюзом нет — ответ воспроизведён из записанных пар.
          <Tag>
            {i18n.t(
              'crm.features.features.ai.chat.components.snippets.viewsnippet.po_zapisannym_primeram',
            )}
          </Tag>
        ) : null}
      </Space>
      {view.agentWidgetId === undefined ? (
        <SaveViewModal
          open={pinOpen}
          spec={spec}
          saving={createView.isPending}
          error={createView.error}
          onCancel={() => setPinOpen(false)}
          onSubmit={async (payload) => {
            const saved = await createView.mutateAsync(payload);
            setSavedViewId(saved.id);
            setPinOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
